/**
 * Painel de Timeline de Eventos
 * Exibe eventos extraídos da transcrição em ordem cronológica
 */

import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import TimelineIcon from '@mui/icons-material/Timeline'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import GavelIcon from '@mui/icons-material/Gavel'
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver'
import VerifiedIcon from '@mui/icons-material/Verified'
import WarningIcon from '@mui/icons-material/Warning'
import HandshakeIcon from '@mui/icons-material/Handshake'
import StarIcon from '@mui/icons-material/Star'
import RefreshIcon from '@mui/icons-material/Refresh'
import FilterListIcon from '@mui/icons-material/FilterList'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { EventTimeline, TimelineEvent, EventCategory } from '../../types/transcription'
import { getEventTimeline } from '../../services/api'
import { contextualErrors, FriendlyError } from '../../services/errorHandler'
import AnalysisNotAvailableCard from './AnalysisNotAvailableCard'

type Props = {
  fileId: string
  onNavigateToSegment?: (segmentIndex: number) => void
}

const CATEGORY_CONFIG: Record<EventCategory, { 
  label: string
  icon: React.ElementType
  color: string
  bgColor: string 
}> = {
  action: {
    label: 'Ação',
    icon: PlayArrowIcon,
    color: '#1976d2',
    bgColor: '#e3f2fd'
  },
  statement: {
    label: 'Declaração',
    icon: RecordVoiceOverIcon,
    color: '#7b1fa2',
    bgColor: '#f3e5f5'
  },
  evidence: {
    label: 'Evidência',
    icon: VerifiedIcon,
    color: '#388e3c',
    bgColor: '#e8f5e9'
  },
  decision: {
    label: 'Decisão',
    icon: GavelIcon,
    color: '#f57c00',
    bgColor: '#fff3e0'
  },
  threat: {
    label: 'Ameaça/Risco',
    icon: WarningIcon,
    color: '#d32f2f',
    bgColor: '#ffebee'
  },
  agreement: {
    label: 'Acordo',
    icon: HandshakeIcon,
    color: '#00796b',
    bgColor: '#e0f2f1'
  }
}

const IMPORTANCE_CONFIG = {
  high: { label: 'Alta', color: '#d32f2f' },
  medium: { label: 'Média', color: '#f57c00' },
  low: { label: 'Baixa', color: '#757575' }
}

/**
 * Formata o tempo para exibição.
 * Aceita tanto segundos (número ou string numérica) quanto formato HH:MM:SS.
 */
function formatTime(time: string): string {
  if (!time) return '0:00'
  
  // Se já está no formato HH:MM:SS ou MM:SS, retorna diretamente (ou simplificado)
  if (time.includes(':')) {
    const parts = time.split(':')
    if (parts.length === 3) {
      // HH:MM:SS - remover horas se for 00
      const hours = parseInt(parts[0], 10)
      const mins = parseInt(parts[1], 10)
      const secs = parseInt(parts[2], 10)
      if (hours > 0) {
        return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
      }
      return `${mins}:${secs.toString().padStart(2, '0')}`
    }
    // MM:SS - retorna como está
    return time
  }
  
  // Se for número de segundos
  const seconds = parseFloat(time)
  if (isNaN(seconds)) return '0:00'
  
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

function EventCard({ 
  event, 
  isLast,
  showConnector,
  onNavigateToSegment
}: { 
  event: TimelineEvent
  isLast: boolean
  showConnector: boolean
  onNavigateToSegment?: (segmentIndex: number) => void
}) {
  const config = CATEGORY_CONFIG[event.category]
  const Icon = config.icon
  const importanceConfig = IMPORTANCE_CONFIG[event.importance]

  return (
    <Box sx={{ display: 'flex', gap: 2 }}>
      {/* Timeline connector */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            bgcolor: config.bgColor,
            border: `3px solid ${config.color}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1
          }}
        >
          <Icon sx={{ color: config.color, fontSize: 22 }} />
        </Box>
        {showConnector && !isLast && (
          <Box
            sx={{
              width: 3,
              flex: 1,
              bgcolor: '#e0e0e0',
              minHeight: 40
            }}
          />
        )}
      </Box>

      {/* Event content */}
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          p: 2,
          mb: 2,
          border: '1px solid #e0e0e0',
          borderLeft: `4px solid ${config.color}`,
          transition: 'all 0.2s',
          '&:hover': {
            boxShadow: 2,
            borderColor: config.color
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" sx={{ 
              bgcolor: '#f5f5f5', 
              px: 1, 
              py: 0.5, 
              borderRadius: 1,
              fontFamily: 'monospace',
              fontWeight: 600
            }}>
              {formatTime(event.timestamp)}
            </Typography>
            <Chip
              label={config.label}
              size="small"
              sx={{ 
                bgcolor: config.bgColor, 
                color: config.color,
                fontWeight: 600,
                fontSize: '0.7rem'
              }}
            />
            {event.importance === 'high' && (
              <Tooltip title="Evento importante">
                <StarIcon sx={{ color: '#ffc107', fontSize: 18 }} />
              </Tooltip>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={importanceConfig.label}
              size="small"
              variant="outlined"
              sx={{ 
                fontSize: '0.65rem',
                color: importanceConfig.color,
                borderColor: importanceConfig.color
              }}
            />
            {onNavigateToSegment && (
              <Tooltip title="Ir para o segmento na transcrição">
                <IconButton
                  size="small"
                  onClick={() => onNavigateToSegment(event.segmentIndex)}
                  sx={{ 
                    color: config.color,
                    '&:hover': { bgcolor: config.bgColor }
                  }}
                >
                  <ArrowForwardIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
          {event.title}
        </Typography>

        <Typography variant="body2" sx={{ color: '#666', mb: 1.5 }}>
          {event.description}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {event.participants.map((p, i) => (
            <Chip
              key={i}
              label={p}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.65rem' }}
            />
          ))}
          {event.mentionedTime && (
            <Chip
              label={`📅 ${event.mentionedTime}`}
              size="small"
              sx={{ 
                fontSize: '0.65rem',
                bgcolor: '#fff3e0',
                color: '#e65100'
              }}
            />
          )}
        </Box>
      </Paper>
    </Box>
  )
}

export default function EventTimelinePanel({ fileId, onNavigateToSegment }: Props) {
  const [timeline, setTimeline] = useState<EventTimeline | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<FriendlyError | null>(null)
  const [filter, setFilter] = useState<EventCategory | 'all' | 'key'>('all')

  const loadTimeline = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await getEventTimeline(fileId)
      setTimeline(data)
    } catch (e: any) {
      const friendlyError = contextualErrors.analysis(e)
      setError(friendlyError)
      console.error('Erro ao carregar timeline:', e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadTimeline()
  }, [fileId])

  if (loading) {
    return (
      <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
        <CardContent sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress size={48} />
          <Typography sx={{ mt: 2, color: '#666' }}>
            Extraindo eventos...
          </Typography>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card elevation={0} sx={{ border: '1px solid #e0e0e0', bgcolor: '#fafafa' }}>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <Box sx={{ fontSize: 48, mb: 2 }}>⚠️</Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            {error.title}
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
            {error.message}
          </Typography>
          {error.canRetry && (
            <Button variant="outlined" onClick={loadTimeline} startIcon={<RefreshIcon />}>
              Tentar novamente
            </Button>
          )}
        </CardContent>
      </Card>
    )
  }

  if (!timeline) {
    return (
      <AnalysisNotAvailableCard 
        title="Timeline de Eventos" 
        onRetry={loadTimeline} 
      />
    )
  }

  const filteredEvents = filter === 'all'
    ? timeline.events
    : filter === 'key'
    ? timeline.keyMoments
    : timeline.eventsByCategory[filter] || []

  const categories = Object.keys(CATEGORY_CONFIG) as EventCategory[]

  return (
    <Box>
      {/* Header com estatísticas */}
      <Card elevation={0} sx={{ border: '1px solid #e0e0e0', mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TimelineIcon sx={{ color: '#1976d2' }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Timeline de Eventos
              </Typography>
            </Box>
            <Button
              startIcon={<RefreshIcon />}
              onClick={loadTimeline}
              size="small"
              sx={{ textTransform: 'none' }}
            >
              Atualizar
            </Button>
          </Box>

          {/* Cards de contagem por categoria */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {categories.map((cat) => {
              const config = CATEGORY_CONFIG[cat]
              const Icon = config.icon
              const count = timeline.eventsByCategory[cat]?.length || 0
              
              return (
                <Grid item xs={6} sm={4} md={2} key={cat}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      textAlign: 'center',
                      border: '1px solid #e0e0e0',
                      cursor: 'pointer',
                      bgcolor: filter === cat ? config.bgColor : '#fff',
                      borderColor: filter === cat ? config.color : '#e0e0e0',
                      transition: 'all 0.2s',
                      '&:hover': {
                        bgcolor: config.bgColor,
                        borderColor: config.color
                      }
                    }}
                    onClick={() => setFilter(cat)}
                  >
                    <Icon sx={{ fontSize: 24, color: config.color }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: config.color }}>
                      {count}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      {config.label}
                    </Typography>
                  </Paper>
                </Grid>
              )
            })}
          </Grid>

          {/* Filtros rápidos */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterListIcon sx={{ color: '#666' }} />
            <Stack direction="row" spacing={1}>
              <Chip
                label={`Todos (${timeline.events.length})`}
                onClick={() => setFilter('all')}
                color={filter === 'all' ? 'primary' : 'default'}
                variant={filter === 'all' ? 'filled' : 'outlined'}
              />
              <Chip
                icon={<StarIcon />}
                label={`Momentos-chave (${timeline.keyMoments.length})`}
                onClick={() => setFilter('key')}
                sx={{
                  bgcolor: filter === 'key' ? '#fff3e0' : undefined,
                  borderColor: filter === 'key' ? '#ffc107' : undefined,
                  '& .MuiChip-icon': { color: '#ffc107' }
                }}
                variant={filter === 'key' ? 'filled' : 'outlined'}
              />
            </Stack>
          </Box>
        </CardContent>
      </Card>

      {/* Timeline */}
      <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 3, fontWeight: 600 }}>
            {filter === 'all' 
              ? `Todos os Eventos (${filteredEvents.length})`
              : filter === 'key'
              ? `Momentos-chave (${filteredEvents.length})`
              : `${CATEGORY_CONFIG[filter].label} (${filteredEvents.length})`
            }
          </Typography>

          <Box sx={{ pl: 1 }}>
            {filteredEvents.map((event, index) => (
              <EventCard
                key={event.id}
                event={event}
                isLast={index === filteredEvents.length - 1}
                showConnector={true}
                onNavigateToSegment={onNavigateToSegment}
              />
            ))}
          </Box>

          {filteredEvents.length === 0 && (
            <Typography sx={{ textAlign: 'center', color: '#666', py: 4 }}>
              Nenhum evento encontrado nesta categoria
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Legenda */}
      <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
        <Typography variant="caption" sx={{ color: '#666' }}>
          💡 <strong>Dica:</strong> A timeline extrai automaticamente eventos mencionados na transcrição.
          Eventos marcados com ⭐ são considerados de alta importância para o contexto.
          Clique em uma categoria para filtrar eventos específicos.
          {onNavigateToSegment && ' Use o botão ➔ para ir diretamente ao segmento na aba Transcrição.'}
        </Typography>
      </Box>
    </Box>
  )
}
